import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthHelperService } from './auth-helper.service';
import { ASKANDY_SUPPORT_EMAIL, CHAT_ERROR_MESSAGES, userFacingChatError } from '../utils/chat-error-messages';
import { detectWebViewContext, shouldPreferInteractiveTurnstile } from '../utils/web-view-context';
import { Capacitor } from '@capacitor/core';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId?: string) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    /** Defined in index.html before api.js loads; drains __askAndyTurnstileOnReadyQueue. */
    __askAndyTurnstileApiReady?: () => void;
    __askAndyTurnstileApiLoaded?: boolean;
    __askAndyTurnstileOnReadyQueue?: Array<() => void>;
  }
}

export interface TurnstileConfig {
  enabled: boolean;
  siteKey: string;
  /** Managed-mode site key for visible checkbox fallback (Invisible keys cannot render size:normal). */
  managedSiteKey: string;
  /** Cloudflare widget type. Default "invisible" — the mode that worked before Managed was enabled. */
  widgetMode: 'managed' | 'invisible';
  requireForAnonymousOnly: boolean;
}

export interface TurnstileTokenResult {
  token: string;
  keyHint?: 'managed';
}

interface CachedToken {
  token: string;
  obtainedAt: number;
  keyHint?: 'managed';
}

/** Prefix for errors that carry a Cloudflare Turnstile client error code. */
const TURNSTILE_CLIENT_ERROR_PREFIX = 'TurnstileClientError:';

interface TurnstileDiagnosticEntry {
  ts: string;
  sessionId: string;
  phase: string;
  outcome: 'start' | 'success' | 'error' | 'escalate' | 'observe';
  errorCode?: string;
  siteKeySuffix?: string;
  keyHint?: string;
  durationMs?: number;
  hostname: string;
  escalationReason?: string;
  details?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TurnstileService {
  private readonly http = inject(HttpClient);
  private readonly authHelper = inject(AuthHelperService);

  private config: TurnstileConfig | null = null;
  private scriptLoadPromise: Promise<void> | null = null;
  private hiddenContainerEl: HTMLElement | null = null;
  private widgetId: string | null = null;
  private cachedToken: CachedToken | null = null;
  private warmEpoch = 0;
  private activeChallenge: Promise<string> | null = null;
  private interactiveOverlayEl: HTMLElement | null = null;
  private lastInteractiveKeyHint: 'managed' | undefined;
  private readonly diagnosticSessionId = `ts-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  private readonly diagnosticTrail: TurnstileDiagnosticEntry[] = [];

  /** Turnstile tokens expire after ~5 minutes; treat as stale earlier to be safe. */
  private readonly tokenMaxAgeMs = 4 * 60 * 1000;
  /** Per-attempt timeout for silent invisible challenges. */
  private readonly challengeTimeoutMs = 30_000;
  /** Visible modal needs longer on mobile Safari / WebView (iframe + PAT). */
  private readonly interactiveChallengeTimeoutMs = 60_000;
  /** In-app WebViews rarely complete Turnstile — fail faster with open-in-browser guidance. */
  private readonly webViewInteractiveChallengeTimeoutMs = 35_000;
  private readonly webViewStallWatchdogMs = 20_000;
  private readonly maxSilentAttempts = 2;
  /** Let Turnstile finish internal retry/reset before we remove the widget (avoids "Nothing to reset"). */
  private readonly widgetTeardownDelayMs = 500;
  private readonly interactiveErrorCleanupDelayMs = 800;

  async getConfig(): Promise<TurnstileConfig> {
    if (this.config) {
      return this.config;
    }

    const fallbackSiteKey = (environment as { turnstileSiteKey?: string }).turnstileSiteKey || '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ security?: { turnstile?: TurnstileConfig } }>(`${environment.apiUrl}/config`)
      );
      const remote = response?.security?.turnstile;
      this.config = {
        enabled: !!remote?.enabled,
        siteKey: remote?.siteKey || fallbackSiteKey,
        managedSiteKey: remote?.managedSiteKey || '',
        widgetMode: remote?.widgetMode === 'managed' ? 'managed' : 'invisible',
        requireForAnonymousOnly: remote?.requireForAnonymousOnly !== false,
      };
      this.warnIfTurnstileKeysMisconfigured(this.config);
    } catch {
      this.config = {
        enabled: !!fallbackSiteKey,
        siteKey: fallbackSiteKey,
        managedSiteKey: '',
        widgetMode: 'invisible',
        requireForAnonymousOnly: true,
      };
    }

    return this.config;
  }

  /** Mirrors server skip logic: anonymous-only when a valid token + email exist locally. */
  async isRequiredForCurrentUser(): Promise<boolean> {
    const config = await this.getConfig();
    if (!config.enabled || !config.siteKey) {
      return false;
    }
    if (config.requireForAnonymousOnly && this.hasAuthenticatedSession()) {
      return false;
    }
    return true;
  }

  private hasAuthenticatedSession(): boolean {
    return !!(this.authHelper.getToken() && this.authHelper.getUserEmail());
  }

  /** Preload Turnstile script and hidden mount early so PAT can warm before first send. */
  async prepareForChat(): Promise<void> {
    if (!(await this.isRequiredForCurrentUser())) {
      return;
    }
    await this.loadScript();
    this.ensureHiddenContainer();
  }

  /** Summary attached to chat requests for container log correlation. */
  getDiagnosticSummary(): { sessionId: string; events: TurnstileDiagnosticEntry[] } {
    return {
      sessionId: this.diagnosticSessionId,
      events: this.diagnosticTrail.slice(-10),
    };
  }

  private siteKeySuffix(siteKey: string): string {
    if (!siteKey) {
      return 'none';
    }
    return siteKey.length <= 8 ? siteKey : siteKey.slice(-8);
  }

  private reportDiagnostic(
    entry: Omit<TurnstileDiagnosticEntry, 'ts' | 'sessionId' | 'hostname'>
  ): void {
    const webView = detectWebViewContext();
    const full: TurnstileDiagnosticEntry = {
      ...entry,
      ts: new Date().toISOString(),
      sessionId: this.diagnosticSessionId,
      hostname: window.location.hostname,
    };
    this.diagnosticTrail.push(full);
    if (this.diagnosticTrail.length > 20) {
      this.diagnosticTrail.shift();
    }
    console.info('[Turnstile][DIAG]', full);
    void this.sendDiagnosticToServer(full, webView);
  }

  private sendDiagnosticToServer(
    entry: TurnstileDiagnosticEntry,
    webView: ReturnType<typeof detectWebViewContext>
  ): void {
    const body = JSON.stringify({
      ...entry,
      webViewKind: webView.webViewKind,
      isInAppWebView: webView.isInAppWebView,
    });
    const url = `${environment.apiUrl}/security/turnstile-diagnostic`;
    try {
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        return;
      }
    } catch {
      // fall through to fetch
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  }

  /** Background warm disabled — it raced with send-time challenges on mobile. */
  warmTokenInBackground(): void {
    return;
  }

  async getTokenForChat(options?: {
    forceFresh?: boolean;
    forceInteractive?: boolean;
  }): Promise<TurnstileTokenResult | undefined> {
    const required = await this.isRequiredForCurrentUser();
    if (!required) {
      return undefined;
    }

    const forceInteractive = options?.forceInteractive === true || this.isVisibleTestMode();

    if (forceInteractive) {
      this.cachedToken = null;
      await this.teardownTurnstileWidget();
    } else {
      if (!options?.forceFresh && this.cachedToken && this.isTokenFresh(this.cachedToken)) {
        const cached = this.cachedToken;
        this.cachedToken = null;
        // Always preserve keyHint — dropping it caused managed tokens to be verified
        // with the invisible secret (invalid-input-response loops on iPhone).
        return cached.keyHint ? { token: cached.token, keyHint: cached.keyHint } : { token: cached.token };
      }
      await this.teardownTurnstileWidget();
    }

    await this.loadScript();
    this.lastInteractiveKeyHint = undefined;
    try {
      const token = await this.withExclusiveChallenge(() => this.getTokenWithRetry(forceInteractive));
      const keyHint = this.lastInteractiveKeyHint;
      return keyHint ? { token, keyHint } : { token };
    } catch (error) {
      throw this.toUserFacingTurnstileError(error);
    }
  }

  private resolveInteractiveSiteKey(config: TurnstileConfig): { siteKey: string; keyHint: 'managed' } {
    if (config.managedSiteKey?.trim()) {
      return { siteKey: config.managedSiteKey.trim(), keyHint: 'managed' };
    }
    throw new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileManagedWidgetMisconfigured));
  }

  private warnIfTurnstileKeysMisconfigured(config: TurnstileConfig): void {
    if (!config.enabled) {
      return;
    }
    if (config.managedSiteKey && config.managedSiteKey === config.siteKey) {
      console.warn(
        '[Turnstile] TURNSTILE_SITE_KEY and TURNSTILE_MANAGED_SITE_KEY are identical. ' +
          'Set TURNSTILE_SITE_KEY to your Invisible widget and TURNSTILE_MANAGED_SITE_KEY to your Managed widget.'
      );
    }
    if (!config.managedSiteKey?.trim()) {
      console.info('[Turnstile] No managed fallback site key — visible modal fallback is disabled.');
    }
  }

  private hasManagedFallback(config: TurnstileConfig): boolean {
    return !!config.managedSiteKey?.trim();
  }

  private clientErrorCode(error: unknown): string | undefined {
    const message = error instanceof Error ? error.message : String(error);
    if (!message.startsWith(TURNSTILE_CLIENT_ERROR_PREFIX)) {
      return undefined;
    }
    return message.slice(TURNSTILE_CLIENT_ERROR_PREFIX.length);
  }

  private rejectWithClientError(reject: (error: Error) => void, errorCode?: string): void {
    reject(new Error(`${TURNSTILE_CLIENT_ERROR_PREFIX}${errorCode || 'unknown'}`));
  }

  private isManagedOnlyInvisibleRejection(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      /Invalid value for parameter "size"/i.test(message) &&
      /invisible/i.test(message)
    );
  }

  /** Cloudflare 600* / 110200 — escalate to visible modal instead of retrying invisible. */
  private shouldEscalateSilentToInteractive(error: unknown, config: TurnstileConfig): boolean {
    if (!this.hasManagedFallback(config)) {
      return false;
    }
    if (this.isManagedOnlyInvisibleRejection(error)) {
      return true;
    }
    const code = this.clientErrorCode(error);
    if (!code) {
      return false;
    }
    return /^600/.test(code) || code === '110200' || code === '110100';
  }

  private startWidgetExecution(widgetId: string): void {
    if (!window.turnstile) {
      return;
    }
    try {
      window.turnstile.execute(widgetId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // Invisible widgets may auto-start on render; treat as in-flight rather than error.
      if (/already executing/i.test(message)) {
        return;
      }
      throw error;
    }
  }

  /** Deferred execution — render does not auto-run; we call execute() once after render. */
  private getSilentChallengeSpec(config: TurnstileConfig): Record<string, unknown> {
    return {
      sitekey: config.siteKey,
      size: 'invisible',
      execution: 'execute',
    };
  }

  private async teardownTurnstileWidget(options?: { removeContainer?: boolean }): Promise<void> {
    this.warmEpoch++;
    this.activeChallenge = null;

    const id = this.widgetId;
    this.widgetId = null;
    if (id && window.turnstile) {
      try {
        window.turnstile.remove(id);
      } catch {
        // Widget may already be removed.
      }
    }

    if (options?.removeContainer !== false && this.hiddenContainerEl) {
      this.hiddenContainerEl.remove();
      this.hiddenContainerEl = null;
    }

    await this.delay(this.widgetTeardownDelayMs);
  }

  /** One reset+execute cycle before treating 600010 as fatal (Cloudflare marks 600* as retryable). */
  private async retrySilentWidgetOnce(widgetId: string): Promise<boolean> {
    if (!window.turnstile) {
      return false;
    }
    try {
      window.turnstile.reset(widgetId);
      await this.delay(300);
      this.startWidgetExecution(widgetId);
      return true;
    } catch {
      return false;
    }
  }

  private async ensureTurnstileLoaded(): Promise<void> {
    await this.loadScript();
    if (!window.turnstile) {
      throw new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileUnavailableClient));
    }
  }

  private toUserFacingTurnstileError(error: unknown): Error {
    if (error instanceof Error) {
      const message = error.message.trim();
      if (message.includes(ASKANDY_SUPPORT_EMAIL)) {
        return error;
      }
      const code = this.clientErrorCode(error);
      if (code === '600010' || (code && /^600/.test(code))) {
        return new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstilePatFailed));
      }
    }
    return new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileChallengeFailed));
  }

  private async waitForNextFrame(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  /**
   * QA hooks — force the visible Turnstile challenge for manual testing only.
   * - `?turnstileVisible=1` — same as server-rejected flow (visible modal)
   * - `?turnstileForceInteractive=1` — skip invisible PAT (matches iPad / WebView behaviour)
   * Does not persist across sessions.
   */
  private isVisibleTestMode(): boolean {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('turnstileVisible') === '1';
    } catch {
      return false;
    }
  }

  private isForceInteractiveTestMode(): boolean {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('turnstileForceInteractive') === '1';
    } catch {
      return false;
    }
  }

  /** Skip invisible PAT where Cloudflare frequently returns 600010 (WebView, iPad, Safari). */
  private shouldSkipSilentChallenge(): boolean {
    if (this.isForceInteractiveTestMode()) {
      return true;
    }
    if (Capacitor.isNativePlatform()) {
      return true;
    }
    return shouldPreferInteractiveTurnstile();
  }

  private interactiveWidgetSize(): 'normal' | 'compact' {
    const narrow = window.matchMedia('(max-width: 768px)').matches;
    const touch = navigator.maxTouchPoints > 1;
    return narrow || touch ? 'compact' : 'normal';
  }

  private logInteractiveTrigger(reason: string): void {
    const routineMobileRoute = reason === 'interactive-preferred-client';
    console.info('[Turnstile] Visible challenge triggered:', reason);
    this.reportDiagnostic({
      phase: routineMobileRoute ? 'routing' : 'escalation',
      outcome: routineMobileRoute ? 'observe' : 'escalate',
      escalationReason: reason,
      keyHint: 'managed',
    });
  }

  /** Only one Turnstile widget at a time — concurrent warm + send was causing hung challenges. */
  private withExclusiveChallenge(run: () => Promise<string>): Promise<string> {
    if (this.activeChallenge) {
      return this.activeChallenge;
    }

    this.activeChallenge = run().finally(() => {
      this.activeChallenge = null;
    });
    return this.activeChallenge;
  }

  private isTokenFresh(cached: CachedToken): boolean {
    return Date.now() - cached.obtainedAt < this.tokenMaxAgeMs;
  }

  private async getTokenWithRetry(forceInteractive = false): Promise<string> {
    const config = await this.getConfig();

    if (forceInteractive) {
      this.logInteractiveTrigger(
        this.isVisibleTestMode() ? 'qa-test-mode' : 'server-rejected-token'
      );
      return this.executeInteractiveChallenge();
    }

    if (this.shouldSkipSilentChallenge() && this.hasManagedFallback(config)) {
      this.logInteractiveTrigger('interactive-preferred-client');
      return this.executeInteractiveChallenge();
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.maxSilentAttempts; attempt++) {
      try {
        return await this.executeSilentChallenge();
      } catch (error) {
        if (this.shouldEscalateSilentToInteractive(error, config)) {
          const code = this.clientErrorCode(error);
          this.logInteractiveTrigger(
            code ? `silent-client-error-${code}` : 'silent-challenge-failed'
          );
          return this.executeInteractiveChallenge();
        }
        lastError = error instanceof Error ? error : new Error(String(error));
        console.warn('[Turnstile] Silent challenge attempt failed:', lastError.message);
        if (attempt < this.maxSilentAttempts) {
          await this.delay(attempt * 1000);
        }
      }
    }

    if (this.hasManagedFallback(config)) {
      this.logInteractiveTrigger('silent-challenge-exhausted');
      return this.executeInteractiveChallenge();
    }

    throw lastError ?? new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileChallengeFailed));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private async loadScript(): Promise<void> {
    if (window.turnstile) {
      return;
    }
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      let settled = false;

      const fail = (error: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        this.scriptLoadPromise = null;
        reject(error);
      };

      const finish = () => {
        if (settled) {
          return;
        }
        if (window.turnstile) {
          settled = true;
          resolve();
        } else {
          fail(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileScriptLoadFailed)));
        }
      };

      if (window.turnstile) {
        resolve();
        return;
      }

      // index.html defines __askAndyTurnstileApiReady before api.js; queue listeners here.
      if (!window.__askAndyTurnstileOnReadyQueue) {
        window.__askAndyTurnstileOnReadyQueue = [];
      }
      window.__askAndyTurnstileOnReadyQueue.push(finish);

      if (window.__askAndyTurnstileApiLoaded) {
        finish();
        return;
      }

      const existing = document.querySelector(
        'script[data-turnstile="true"], script[src*="challenges.cloudflare.com/turnstile"]'
      );
      if (existing) {
        existing.addEventListener('load', () => finish(), { once: true });
        existing.addEventListener(
          'error',
          () => fail(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileScriptLoadFailed))),
          { once: true }
        );
        return;
      }

      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__askAndyTurnstileApiReady';
      script.async = true;
      script.defer = true;
      script.dataset['turnstile'] = 'true';
      script.onerror = () =>
        fail(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileScriptLoadFailed)));
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  /** Full script reload after Turnstile 300* widget-crash loops (soft reset is not enough). */
  private async reloadTurnstileScript(): Promise<void> {
    document
      .querySelectorAll('script[data-turnstile="true"], script[src*="challenges.cloudflare.com/turnstile"]')
      .forEach((el) => el.remove());
    delete window.turnstile;
    window.__askAndyTurnstileApiLoaded = false;
    window.__askAndyTurnstileOnReadyQueue = [];
    this.scriptLoadPromise = null;
    await this.loadScript();
  }

  /** Reuse a stable off-screen container (original working integration). */
  private ensureHiddenContainer(): HTMLElement {
    if (this.hiddenContainerEl && document.body.contains(this.hiddenContainerEl)) {
      return this.hiddenContainerEl;
    }

    const container = document.createElement('div');
    container.id = 'askandy-turnstile-container';
    container.setAttribute('aria-hidden', 'true');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.width = '0';
    container.style.height = '0';
    container.style.overflow = 'hidden';
    document.body.appendChild(container);
    this.hiddenContainerEl = container;
    return container;
  }

  private async executeSilentChallenge(): Promise<string> {
    const config = await this.getConfig();
    if (!config.siteKey) {
      throw new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileNotConfigured));
    }

    await this.ensureTurnstileLoaded();
    await this.teardownTurnstileWidget({ removeContainer: false });

    const container = this.ensureHiddenContainer();
    const renderOptions = this.getSilentChallengeSpec(config);
    const startedAt = Date.now();
    this.reportDiagnostic({
      phase: 'silent',
      outcome: 'start',
      siteKeySuffix: this.siteKeySuffix(config.siteKey),
      keyHint: 'invisible',
    });

    return new Promise<string>((resolve, reject) => {
      let retried600 = false;

      const timeout = window.setTimeout(() => {
        void this.teardownTurnstileWidget({ removeContainer: false }).then(() => {
          reject(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileTimedOut)));
        });
      }, this.challengeTimeoutMs);

      const clear = () => window.clearTimeout(timeout);

      const finishSuccess = (token: string) => {
        clear();
        this.reportDiagnostic({
          phase: 'silent',
          outcome: 'success',
          siteKeySuffix: this.siteKeySuffix(config.siteKey),
          keyHint: 'invisible',
          durationMs: Date.now() - startedAt,
        });
        void this.teardownTurnstileWidget({ removeContainer: false }).then(() => resolve(token));
      };

      const finishClientError = (errorCode?: string, widgetId?: string) => {
        const is600 = errorCode === '600010' || (errorCode && /^600/.test(errorCode));
        if (
          !retried600 &&
          widgetId &&
          is600 &&
          !this.hasManagedFallback(config)
        ) {
          retried600 = true;
          this.reportDiagnostic({
            phase: 'silent',
            outcome: 'error',
            errorCode: `${errorCode || 'unknown'}-retry`,
            siteKeySuffix: this.siteKeySuffix(config.siteKey),
            keyHint: 'invisible',
            durationMs: Date.now() - startedAt,
          });
          void this.retrySilentWidgetOnce(widgetId);
          return;
        }

        clear();
        this.reportDiagnostic({
          phase: 'silent',
          outcome: 'error',
          errorCode: errorCode || 'unknown',
          siteKeySuffix: this.siteKeySuffix(config.siteKey),
          keyHint: 'invisible',
          durationMs: Date.now() - startedAt,
        });
        void this.teardownTurnstileWidget({ removeContainer: false }).then(() => {
          this.rejectWithClientError(reject, errorCode);
        });
      };

      try {
        this.widgetId = window.turnstile!.render(container, {
          ...renderOptions,
          callback: (token: string) => finishSuccess(token),
          'error-callback': (errorCode?: string) => finishClientError(errorCode, this.widgetId || undefined),
          'timeout-callback': () => {
            clear();
            void this.teardownTurnstileWidget({ removeContainer: false }).then(() => {
              reject(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileTimedOut)));
            });
          },
        });
        this.startWidgetExecution(this.widgetId);
      } catch (error) {
        clear();
        void this.teardownTurnstileWidget({ removeContainer: false }).then(() => {
          if (this.isManagedOnlyInvisibleRejection(error)) {
            reject(error instanceof Error ? error : new Error(String(error)));
            return;
          }
          reject(this.toUserFacingTurnstileError(error));
        });
      }
    });
  }

  /**
   * Visible fallback for browsers / networks where the invisible widget fails
   * (Safari, in-app WebViews, strict privacy settings, high-latency regions).
   * Requires a Managed Turnstile widget site key — Invisible-only keys cannot render size:normal.
   */
  private openInExternalBrowser(): void {
    const url = window.location.href;
    try {
      // Capacitor / common hybrid bridges
      const w = window as Window & { cordova?: { InAppBrowser?: { open: (u: string, t: string) => void } } };
      if (w.cordova?.InAppBrowser?.open) {
        w.cordova.InAppBrowser.open(url, '_system');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Fallback: copy-friendly status is shown in the modal.
    }
  }

  private async executeInteractiveChallenge(): Promise<string> {
    const config = await this.getConfig();
    const { siteKey, keyHint } = this.resolveInteractiveSiteKey(config);
    if (!siteKey) {
      throw new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileNotConfigured));
    }
    this.lastInteractiveKeyHint = keyHint;

    await this.teardownTurnstileWidget({ removeContainer: true });
    await this.ensureTurnstileLoaded();
    this.removeInteractiveOverlay();

    const webView = detectWebViewContext();
    const isInAppWebView = webView.isInAppWebView;
    const challengeTimeoutMs = isInAppWebView
      ? this.webViewInteractiveChallengeTimeoutMs
      : this.interactiveChallengeTimeoutMs;

    const overlay = document.createElement('div');
    overlay.className = 'askandy-turnstile-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Security verification');
    overlay.style.cssText =
      'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;';

    const panel = document.createElement('div');
    panel.style.cssText =
      'background:#fff;color:#0f172a;padding:24px;border-radius:12px;max-width:420px;width:100%;box-shadow:0 20px 40px rgba(0,0,0,0.2);text-align:center;font-family:system-ui,-apple-system,sans-serif;';

    const title = document.createElement('h2');
    title.textContent = 'Quick security check';
    title.style.cssText = 'margin:0 0 8px;font-size:1.125rem;font-weight:600;';

    const body = document.createElement('p');
    body.textContent = isInAppWebView
      ? 'Please confirm you are human to continue. If the check does not load, open AskAndy in Chrome or Safari.'
      : 'Please confirm you are human to continue. This helps keep AskAndy safe for everyone.';
    body.style.cssText = 'margin:0 0 16px;font-size:0.9375rem;line-height:1.5;color:#475569;';

    const statusEl = document.createElement('p');
    statusEl.textContent = 'Loading verification…';
    statusEl.style.cssText = 'margin:0 0 12px;font-size:0.875rem;line-height:1.5;color:#64748b;';

    const widgetHost = document.createElement('div');
    widgetHost.className = 'askandy-turnstile-widget-host';
    widgetHost.style.cssText =
      'display:flex;justify-content:center;align-items:center;min-height:78px;min-width:300px;width:100%;margin:8px 0 16px;';

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.textContent = 'Try again';
    retryBtn.style.cssText =
      'display:none;margin-right:8px;border:1px solid #2563eb;background:#2563eb;color:#fff;border-radius:8px;padding:8px 16px;font-size:0.875rem;cursor:pointer;';

    const openBrowserBtn = document.createElement('button');
    openBrowserBtn.type = 'button';
    openBrowserBtn.textContent = 'Open in browser';
    openBrowserBtn.style.cssText =
      'display:none;margin-right:8px;border:1px solid #0f766e;background:#0f766e;color:#fff;border-radius:8px;padding:8px 16px;font-size:0.875rem;cursor:pointer;';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText =
      'border:1px solid #cbd5e1;background:#fff;color:#334155;border-radius:8px;padding:8px 16px;font-size:0.875rem;cursor:pointer;';

    panel.append(title, body, statusEl, widgetHost, retryBtn, openBrowserBtn, cancelBtn);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    this.interactiveOverlayEl = overlay;

    await this.waitForNextFrame();
    const startedAt = Date.now();
    this.reportDiagnostic({
      phase: 'interactive',
      outcome: 'start',
      siteKeySuffix: this.siteKeySuffix(siteKey),
      keyHint: keyHint || 'managed',
    });

    return new Promise<string>((resolve, reject) => {
      let settled = false;
      let autoHardResets = 0;
      let mountCheckTimer: ReturnType<typeof window.setTimeout> | null = null;
      let stallWatchdogTimer: ReturnType<typeof window.setTimeout> | null = null;
      let hardResetInProgress = false;
      // WebViews: one auto-reset only; then show open-in-browser (300010 rarely recovers in wv).
      const maxAutoHardResets = isInAppWebView ? 1 : 1;
      const mountCheckDelayMs = isInAppWebView
        ? 10_000
        : /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)
          ? 20_000
          : 12_000;
      const stallWatchdogMs = isInAppWebView ? this.webViewStallWatchdogMs : 45_000;

      const finish = (handler: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        if (mountCheckTimer !== null) {
          window.clearTimeout(mountCheckTimer);
          mountCheckTimer = null;
        }
        if (stallWatchdogTimer !== null) {
          window.clearTimeout(stallWatchdogTimer);
          stallWatchdogTimer = null;
        }
        handler();
      };

      const cleanup = () => {
        window.setTimeout(() => {
          void this.teardownTurnstileWidget({ removeContainer: true });
          this.removeInteractiveOverlay();
        }, this.interactiveErrorCleanupDelayMs);
      };

      const timeout = window.setTimeout(() => {
        if (settled) {
          return;
        }
        this.reportDiagnostic({
          phase: 'interactive',
          outcome: 'error',
          errorCode: 'challenge-timeout',
          siteKeySuffix: this.siteKeySuffix(siteKey),
          keyHint: keyHint || 'managed',
          durationMs: Date.now() - startedAt,
        });
        showInteractiveRetry('challenge-timeout');
      }, challengeTimeoutMs);

      const clear = () => window.clearTimeout(timeout);

      cancelBtn.addEventListener('click', () => {
        finish(() => {
          clear();
          cleanup();
          reject(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileCancelled)));
        });
      });

      let widgetHostEl = widgetHost;

      const replaceWidgetHost = (): HTMLElement => {
        const fresh = document.createElement('div');
        fresh.className = 'askandy-turnstile-widget-host';
        fresh.style.cssText = widgetHostEl.style.cssText;
        widgetHostEl.replaceWith(fresh);
        widgetHostEl = fresh;
        return fresh;
      };

      const failureHint = (errorCode?: string): string => {
        const ua = navigator.userAgent;
        const isFirefox = /Firefox|Fennec|Gecko\//i.test(ua);
        const isAndroid = /Android/i.test(ua);
        const is300 = !!(errorCode && /^300/.test(errorCode));
        if (isInAppWebView) {
          return CHAT_ERROR_MESSAGES.turnstileInAppWebView;
        }
        if (is300) {
          return 'Verification crashed in this browser. Tap Try again, or open AskAndy in Chrome or Safari.';
        }
        if (errorCode === '600010' || (errorCode && /^600/.test(errorCode))) {
          if (isFirefox) {
            return 'Firefox blocked automatic verification. Tap Try again, or turn off Enhanced Tracking Protection for this site and refresh.';
          }
          if (isAndroid) {
            return 'Verification was blocked. Tap Try again, or allow challenges.cloudflare.com and refresh.';
          }
          return 'Automatic verification failed. Tap Try again, or disable content blockers / VPN and refresh.';
        }
        if (errorCode === 'challenge-timeout' || errorCode === 'verification-stalled') {
          return 'Verification timed out. Tap Try again, or refresh the page with ad blockers and VPN off.';
        }
        return 'Verification could not load. Tap Try again, or refresh with ad blockers and VPN off.';
      };

      const showInteractiveRetry = (errorCode?: string): void => {
        if (settled) {
          return;
        }
        hardResetInProgress = false;
        statusEl.textContent = failureHint(errorCode);
        statusEl.style.color = '#b45309';
        retryBtn.style.display = 'inline-block';
        openBrowserBtn.style.display = isInAppWebView ? 'inline-block' : 'none';
      };

      const hardResetInteractive = async (reloadScript: boolean): Promise<void> => {
        if (mountCheckTimer !== null) {
          window.clearTimeout(mountCheckTimer);
          mountCheckTimer = null;
        }
        await this.teardownTurnstileWidget({ removeContainer: true });
        if (reloadScript) {
          await this.reloadTurnstileScript();
        } else {
          await this.ensureTurnstileLoaded();
        }
        await this.delay(reloadScript ? 800 : 500);
        replaceWidgetHost();
      };

      const scheduleInteractiveHardReset = (reloadScript: boolean, userInitiated: boolean): void => {
        if (settled || hardResetInProgress) {
          return;
        }
        if (!userInitiated && autoHardResets >= maxAutoHardResets) {
          showInteractiveRetry();
          return;
        }
        hardResetInProgress = true;
        if (!userInitiated) {
          autoHardResets++;
        }
        statusEl.textContent = 'Restarting verification…';
        retryBtn.style.display = 'none';
        statusEl.style.color = '#64748b';

        const resetTimeout = window.setTimeout(() => {
          if (!settled) {
            showInteractiveRetry('hard-reset-timeout');
          }
        }, 20_000);

        void hardResetInteractive(reloadScript)
          .then(() => {
            window.clearTimeout(resetTimeout);
            if (!settled) {
              hardResetInProgress = false;
              renderInteractiveWidget();
            }
          })
          .catch(() => {
            window.clearTimeout(resetTimeout);
            showInteractiveRetry('hard-reset-failed');
          });
      };

      const armStallWatchdog = (): void => {
        if (stallWatchdogTimer !== null) {
          window.clearTimeout(stallWatchdogTimer);
        }
        stallWatchdogTimer = window.setTimeout(() => {
          if (settled) {
            return;
          }
          this.reportDiagnostic({
            phase: 'interactive',
            outcome: 'error',
            errorCode: 'verification-stalled',
            siteKeySuffix: this.siteKeySuffix(siteKey),
            keyHint: keyHint || 'managed',
            durationMs: Date.now() - startedAt,
          });
          showInteractiveRetry('verification-stalled');
        }, stallWatchdogMs);
      };

      const renderInteractiveWidget = (): void => {
        if (mountCheckTimer !== null) {
          window.clearTimeout(mountCheckTimer);
          mountCheckTimer = null;
        }
        widgetHostEl.replaceChildren();
        retryBtn.style.display = 'none';
        statusEl.style.color = '#64748b';
        statusEl.textContent = 'Complete the check below to continue.';
        this.widgetId = window.turnstile!.render(widgetHostEl, {
          sitekey: siteKey,
          size: this.interactiveWidgetSize(),
          theme: 'auto',
          appearance: 'always',
          retry: 'auto',
          'refresh-expired': 'auto',
          callback: (token: string) => {
            finish(() => {
              clear();
              this.reportDiagnostic({
                phase: 'interactive',
                outcome: 'success',
                siteKeySuffix: this.siteKeySuffix(siteKey),
                keyHint: keyHint || 'managed',
                durationMs: Date.now() - startedAt,
              });
              statusEl.textContent = '';
              window.setTimeout(() => {
                void this.teardownTurnstileWidget({ removeContainer: true });
                this.removeInteractiveOverlay();
              }, 0);
              resolve(token);
            });
          },
          'error-callback': (errorCode?: string) => {
            console.warn('[Turnstile] Interactive challenge error:', errorCode);
            this.reportDiagnostic({
              phase: 'interactive',
              outcome: 'error',
              errorCode: errorCode || 'unknown',
              siteKeySuffix: this.siteKeySuffix(siteKey),
              keyHint: keyHint || 'managed',
              durationMs: Date.now() - startedAt,
            });
            if (settled) {
              return;
            }
            if (stallWatchdogTimer !== null) {
              window.clearTimeout(stallWatchdogTimer);
              stallWatchdogTimer = null;
            }
            if (errorCode && /^300/.test(errorCode)) {
              // Android/iOS WebViews often crash Turnstile (300010); one reset then open-in-browser.
              if (isInAppWebView && autoHardResets >= 1) {
                showInteractiveRetry(errorCode);
                return;
              }
              scheduleInteractiveHardReset(autoHardResets >= 1, false);
              return;
            }
            showInteractiveRetry(errorCode);
          },
          'timeout-callback': () => {
            if (settled) {
              return;
            }
            this.reportDiagnostic({
              phase: 'interactive',
              outcome: 'error',
              errorCode: 'widget-timeout',
              siteKeySuffix: this.siteKeySuffix(siteKey),
              keyHint: keyHint || 'managed',
              durationMs: Date.now() - startedAt,
            });
            showInteractiveRetry('widget-timeout');
          },
        });

        armStallWatchdog();

        mountCheckTimer = window.setTimeout(() => {
          mountCheckTimer = null;
          if (settled) {
            return;
          }
          const mounted =
            widgetHostEl.querySelector('iframe, .cf-turnstile, input[type="checkbox"]') !== null;
          if (!mounted) {
            this.reportDiagnostic({
              phase: 'interactive',
              outcome: 'error',
              errorCode: 'widget-not-mounted',
              siteKeySuffix: this.siteKeySuffix(siteKey),
              keyHint: keyHint || 'managed',
              durationMs: Date.now() - startedAt,
            });
            showInteractiveRetry('widget-not-mounted');
          }
        }, mountCheckDelayMs);
      };

      retryBtn.addEventListener('click', () => {
        if (settled) {
          return;
        }
        statusEl.textContent = 'Loading verification…';
        openBrowserBtn.style.display = 'none';
        scheduleInteractiveHardReset(autoHardResets >= 1, true);
      });

      openBrowserBtn.addEventListener('click', () => {
        this.openInExternalBrowser();
        finish(() => {
          clear();
          cleanup();
          reject(new Error(userFacingChatError(CHAT_ERROR_MESSAGES.turnstileInAppWebView)));
        });
      });

      try {
        renderInteractiveWidget();
      } catch (error) {
        finish(() => {
          clear();
          cleanup();
          reject(error instanceof Error ? error : new Error(String(error)));
        });
      }
    });
  }

  private removeInteractiveOverlay(): void {
    if (this.interactiveOverlayEl) {
      this.interactiveOverlayEl.remove();
      this.interactiveOverlayEl = null;
    }
  }
}
